/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CompanyAddress } from 'types/entities/CompanyAddress'
import { UiTableActionsCell, UiTableCell, UiTableRow, } from 'components/Ui/UiTable'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid'
import { getLocalizedCountryName } from 'utils/localizedCountryUtils'

interface EditCompanyModalAddressItemProps {
    address: CompanyAddress
    onEdit: (address: CompanyAddress) => void
    onDelete: (address: CompanyAddress) => void
}

const EditCompanyModalAddressItem: React.FC<
    EditCompanyModalAddressItemProps
> = ({ address, onEdit, onDelete }) => {
    const { t, i18n } = useTranslation()

    const localizedCountry = getLocalizedCountryName(address.country, i18n.language)

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation()
        onEdit(address)
    }

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation()
        onDelete(address)
    }

    return (
        <UiTableRow className="EditCompanyModalAddressItem" key={address.id}>
            <UiTableCell>
                {address.name || <span className="text-gray-400">-</span>}
            </UiTableCell>
            <UiTableCell>
                {address.department_name ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        {address.department_name}
                    </span>
                ) : (
                    <span className="text-gray-400">-</span>
                )}
            </UiTableCell>
            <UiTableCell>
                {address.street_name || (
                    <span className="text-gray-400">-</span>
                )}
            </UiTableCell>
            <UiTableCell>
                {address.town || <span className="text-gray-400">-</span>}
            </UiTableCell>
            <UiTableCell>
                {localizedCountry || <span className="text-gray-400">-</span>}
            </UiTableCell>
            <UiTableCell>
                {address.note ? (
                    <div className="max-w-xs truncate" title={address.note}>
                        {address.note}
                    </div>
                ) : (
                    <span className="text-gray-400">-</span>
                )}
            </UiTableCell>
            <UiTableActionsCell>
                <button
                    onClick={handleEdit}
                    className="p-1.5 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 rounded transition-colors shrink-0"
                    title={t('Edit') as string}
                >
                    <PencilIcon className="w-4 h-4 text-white" />
                </button>
                <button
                    onClick={handleDelete}
                    className="p-1.5 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 rounded transition-colors shrink-0"
                    title={t('Delete') as string}
                >
                    <TrashIcon className="w-4 h-4 text-white" />
                </button>
            </UiTableActionsCell>
        </UiTableRow>
    )
}

export default EditCompanyModalAddressItem
